import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, Loader2, Send, MessageCircle } from "lucide-react";

const THREAD_STORAGE_KEY = "kd_support_thread";
const IDENTITY_STORAGE_KEY = "kd_support_identity";

export default function SupportDrawer({ open, onClose, supportEmail = "" }) {
  const [phase, setPhase] = useState("identify"); // "identify" | "chat"
  const [identity, setIdentity] = useState(() => {
    try { return JSON.parse(localStorage.getItem(IDENTITY_STORAGE_KEY) || "null") || { email: "", name: "" }; }
    catch { return { email: "", name: "" }; }
  });
  const [threadId, setThreadId] = useState(() => localStorage.getItem(THREAD_STORAGE_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  // Move to chat phase if we already have thread + identity email
  useEffect(() => {
    if (open && threadId && identity.email) setPhase("chat");
    else if (open) setPhase("identify");
  }, [open, threadId, identity.email]);

  // Poll for new messages while open
  useEffect(() => {
    if (!open || phase !== "chat" || !threadId) return;
    const load = async () => {
      try {
        const { data } = await api.get(`/support/threads/${threadId}/messages`);
        setMessages(data.messages || []);
      } catch { /* silent */ }
    };
    load();
    pollRef.current = setInterval(load, 3500);
    return () => clearInterval(pollRef.current);
  }, [open, phase, threadId]);

  // Auto-scroll to newest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, phase]);

  const startChat = async (e) => {
    e.preventDefault();
    if (!identity.email) { toast.error("Please enter your email"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/support/thread", identity);
      setThreadId(data.thread_id);
      localStorage.setItem(THREAD_STORAGE_KEY, data.thread_id);
      localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity));
      setPhase("chat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start chat");
    } finally { setStarting(false); }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || !threadId) return;
    setSending(true);
    // optimistic add
    const optimistic = { id: `local-${Date.now()}`, sender: "user", text: t, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    setText("");
    try {
      await api.post(`/support/threads/${threadId}/messages`, { thread_id: threadId, text: t });
    } catch (err) {
      toast.error("Could not send — please retry");
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setText(t);
    } finally { setSending(false); }
  };

  const resetChat = () => {
    localStorage.removeItem(THREAD_STORAGE_KEY);
    setThreadId("");
    setMessages([]);
    setPhase("identify");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: 480, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 480, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            data-testid="support-drawer"
            className="fixed top-4 bottom-4 right-4 z-[101] w-[min(94vw,440px)] kd-glass-strong rounded-3xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#E50914]/15 border border-[#E50914]/40 flex items-center justify-center">
                  <MessageCircle size={16} className="text-[#FF2A2D]" />
                </div>
                <div>
                  <div className="kd-display text-lg leading-tight">Live chat</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">KlickDeck team</div>
                </div>
              </div>
              <button onClick={onClose} data-testid="support-close" className="rounded-full p-2 bg-white/5 hover:bg-white/10 border border-white/10">
                <X size={14} />
              </button>
            </div>

            {phase === "identify" && (
              <form onSubmit={startChat} className="p-6 flex-1 flex flex-col">
                <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
                  Say hi — we&apos;ll reply here in real time. If nobody&apos;s around, the transcript is sent to <span className="text-white">{supportEmail || "our support inbox"}</span> and we&apos;ll email you back.
                </p>
                <div className="space-y-3">
                  <input
                    required type="email" placeholder="Your email"
                    value={identity.email}
                    onChange={(e) => setIdentity({ ...identity, email: e.target.value })}
                    data-testid="chat-email"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30 placeholder:text-zinc-600"
                  />
                  <input
                    placeholder="Your name (optional)"
                    value={identity.name}
                    onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
                    data-testid="chat-name"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30 placeholder:text-zinc-600"
                  />
                </div>
                <button
                  type="submit" disabled={starting} data-testid="chat-start"
                  className="mt-6 w-full rounded-full py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {starting ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} Start chat
                </button>
                {supportEmail && (
                  <div className="text-[11px] text-zinc-500 mt-4 text-center">
                    Or email us at <a href={`mailto:${supportEmail}`} className="text-white hover:text-[#FF2A2D] transition-colors">{supportEmail}</a>
                  </div>
                )}
              </form>
            )}

            {phase === "chat" && (
              <>
                <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-5 space-y-3">
                  {messages.length === 0 && (
                    <div className="text-center text-zinc-500 text-xs py-10">
                      Say hello — we usually reply in a couple of minutes.
                    </div>
                  )}
                  {messages.map((m) => {
                    const isUser = m.sender === "user";
                    return (
                      <div key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                            isUser
                              ? "bg-[#E50914] text-white rounded-br-sm"
                              : "bg-white/[0.05] text-white border border-white/[0.08] rounded-bl-sm"
                          }`}
                        >
                          {!isUser && (
                            <div className="text-[10px] tracking-[0.2em] uppercase text-[#FF2A2D] mb-1">KlickDeck</div>
                          )}
                          <div className="whitespace-pre-wrap break-words leading-relaxed">{m.text}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <form onSubmit={send} className="p-4 border-t border-white/[0.06] flex items-end gap-2">
                  <textarea
                    rows={1}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); }
                    }}
                    placeholder="Type a message…"
                    data-testid="chat-input"
                    className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/30 placeholder:text-zinc-600 resize-none max-h-32"
                  />
                  <button
                    type="submit" disabled={sending || !text.trim()} data-testid="chat-send"
                    className="rounded-full p-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 disabled:opacity-40"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  </button>
                </form>
                <div className="px-4 pb-3 flex items-center justify-between text-[10px] text-zinc-500">
                  <span>Live · {identity.email}</span>
                  <button type="button" onClick={resetChat} data-testid="chat-reset" className="hover:text-white transition-colors">End chat</button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
